import Hls, { type ErrorData, type Level } from "hls.js";
import type { PlayerCallbacks, PlayerInstance, DebugStats, LevelInfo, BitrateDataPoint } from "./player-types";
import { createEmptyDebugStats } from "./player-types";

/**
 * HLS.js player for Chrome, Firefox, and other browsers without native HLS support.
 * Provides low-latency live streaming with adaptive bitrate.
 */
export class HlsJsPlayer implements PlayerInstance {
  private video: HTMLVideoElement;
  private callbacks: PlayerCallbacks;
  private hls: Hls | null = null;

  private debugStats: DebugStats = createEmptyDebugStats();
  private debugStartTime = 0;

  private lastRecoveryAttempt: number | null = null;
  private recoveryAttempts = 0;

  // Stall watchdog state
  private stallWatchdogInterval: number | null = null;
  private lastCurrentTime = 0;
  private stallStartTime: number | null = null;
  private stallRecoveryAttempts = 0;

  private readonly BITRATE_HISTORY_MAX_POINTS = 120; // 60 seconds at 500ms intervals
  private readonly RECOVERY_COOLDOWN_MS = 5000;
  private readonly MAX_RECOVERY_ATTEMPTS = 3;
  private readonly STALL_CHECK_INTERVAL_MS = 1000;
  private readonly STALL_THRESHOLD_MS = 2000;
  private readonly MAX_STALL_RECOVERY_ATTEMPTS = 3;

  constructor(video: HTMLVideoElement, callbacks: PlayerCallbacks) {
    this.video = video;
    this.callbacks = callbacks;
  }

  setup(): void {
    this.debugStartTime = performance.now();

    this.hls = new Hls({
      // Low Latency Core
      lowLatencyMode: true,

      // Live Sync (more conservative for 1s segments with slow networks)
      liveSyncDurationCount: 3,
      liveMaxLatencyDurationCount: 6,
      maxLiveSyncPlaybackRate: 1.2,
      liveSyncOnStallIncrease: 1,

      // Buffer Management (increased for network variability)
      backBufferLength: 15,
      maxBufferLength: 8,
      maxMaxBufferLength: 15,
      maxBufferHole: 0.5,

      // ABR Tuning - more conservative to avoid buffer underruns
      abrEwmaFastLive: 2.0,
      abrEwmaSlowLive: 6.0,
      abrBandWidthFactor: 0.6,
      abrBandWidthUpFactor: 0.4,

      // Segment Loading
      startFragPrefetch: true,
      maxFragLookUpTolerance: 0.2,

      // Stall Recovery
      maxStarvationDelay: 3,
      nudgeOffset: 0.1,
      nudgeMaxRetry: 5,

      // Watchdog
      highBufferWatchdogPeriod: 2,
    });

    this.hls.attachMedia(this.video);
    this.hls.loadSource(this.callbacks.getStreamUrl());

    this.hls.on(Hls.Events.MANIFEST_PARSED, this.handleManifestParsed);
    this.hls.on(Hls.Events.ERROR, this.handleError);
    this.hls.on(Hls.Events.FRAG_BUFFERED, this.handleFragBuffered);

    this.video.addEventListener("playing", this.handlePlaying);
    this.video.addEventListener("loadedmetadata", this.handleLoadedMetadata);
    this.video.addEventListener("ended", this.handleEnded);
    this.video.addEventListener("resize", this.handleVideoResize);
  }

  cleanup(): void {
    this.stopStallWatchdog();

    if (this.hls) {
      this.hls.destroy();
      this.hls = null;
    }

    this.video.removeEventListener("playing", this.handlePlaying);
    this.video.removeEventListener("loadedmetadata", this.handleLoadedMetadata);
    this.video.removeEventListener("ended", this.handleEnded);
    this.video.removeEventListener("resize", this.handleVideoResize);

    // Reset video element to stop any buffered audio/video playback
    this.video.pause();
    this.video.src = "";
    this.video.load();

    this.debugStats = createEmptyDebugStats();
    this.recoveryAttempts = 0;
    this.lastRecoveryAttempt = null;
    this.stallRecoveryAttempts = 0;
    this.stallStartTime = null;
  }

  getDebugStats(): DebugStats {
    this.updateDebugStats();
    return this.debugStats;
  }

  async safePlay(): Promise<void> {
    try {
      await this.video.play();
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          this.callbacks.onStateChange("autoplay-blocked");
          return;
        }
        // AbortError: play() interrupted by source change - expected during cleanup/reinitialization
        if (error.name === "AbortError") {
          return;
        }
      }
      throw error;
    }
  }

  getHlsInstance(): Hls | null {
    return this.hls;
  }

  // ==================== Event Handlers ====================

  private handleManifestParsed = (): void => {
    // Early aspect ratio detection from manifest RESOLUTION tags (before video frames)
    const firstLevel = this.hls?.levels[0];
    if (firstLevel && firstLevel.height > 0 && firstLevel.width > 0) {
      const ratio = firstLevel.height > firstLevel.width ? "portrait" : "landscape";
      this.callbacks.onAspectRatioDetected(ratio);
    }

    this.callbacks.onStateChange("playing");
    this.detectAspectRatio();
    this.startStallWatchdog();
    void this.safePlay();
  };

  private handlePlaying = (): void => {
    this.callbacks.onStateChange("playing");
    this.recoveryAttempts = 0;
    this.lastRecoveryAttempt = null;
    this.stallStartTime = null;
    this.stallRecoveryAttempts = 0;
  };

  private handleLoadedMetadata = (): void => {
    this.detectAspectRatio();
  };

  private handleEnded = (): void => {
    this.callbacks.onStreamEnded();
  };

  private handleVideoResize = (): void => {
    const width = this.video.videoWidth;
    const height = this.video.videoHeight;
    if (width > 0 && height > 0) {
      const ratio = height > width ? "portrait" : "landscape";
      this.callbacks.onAspectRatioDetected(ratio);
    }
  };

  private handleError = (_event: typeof Hls.Events.ERROR, data: ErrorData): void => {
    // Track errors for debug stats
    if (data.details === "bufferStalledError") {
      this.debugStats = {
        ...this.debugStats,
        bufferStalledErrors: this.debugStats.bufferStalledErrors + 1,
      };
    } else if (data.details === "fragLoadError") {
      this.debugStats = {
        ...this.debugStats,
        fragLoadErrors: this.debugStats.fragLoadErrors + 1,
      };
    }

    if (data.fatal) {
      const now = Date.now();
      const canAttemptRecovery =
        !this.lastRecoveryAttempt || now - this.lastRecoveryAttempt > this.RECOVERY_COOLDOWN_MS;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          if (
            data.details === "manifestLoadError" ||
            data.details === "manifestParsingError" ||
            data.details === "levelLoadError"
          ) {
            this.cleanup();
            this.callbacks.onWaitForStream();
          } else {
            this.hls?.startLoad();
          }
          break;

        case Hls.ErrorTypes.MEDIA_ERROR:
          if (canAttemptRecovery && this.recoveryAttempts < this.MAX_RECOVERY_ATTEMPTS) {
            console.warn(
              `HLS.js media error, recovery attempt ${this.recoveryAttempts + 1}/${this.MAX_RECOVERY_ATTEMPTS}`,
            );
            this.lastRecoveryAttempt = now;
            this.recoveryAttempts++;
            this.hls?.recoverMediaError();
          } else if (this.recoveryAttempts >= this.MAX_RECOVERY_ATTEMPTS) {
            console.warn("HLS.js: Max recovery attempts reached. Reloading stream.");
            this.reloadStream();
          }
          break;

        default:
          // Handle OTHER_ERROR (includes BUFFER_STALLED_ERROR)
          if (data.details === "bufferStalledError") {
            if (canAttemptRecovery && this.recoveryAttempts < this.MAX_RECOVERY_ATTEMPTS) {
              console.warn(
                `HLS.js buffer stalled, recovery attempt ${this.recoveryAttempts + 1}/${this.MAX_RECOVERY_ATTEMPTS}`,
              );
              this.lastRecoveryAttempt = now;
              this.recoveryAttempts++;
              this.hls?.recoverMediaError();
            } else if (this.recoveryAttempts >= this.MAX_RECOVERY_ATTEMPTS) {
              console.warn("HLS.js: Max stall recovery attempts. Reloading stream.");
              this.reloadStream();
            }
          } else {
            this.callbacks.onError("Fatal playback error. Please retry.");
            this.cleanup();
          }
          break;
      }
    }
  };

  private reloadStream(): void {
    this.recoveryAttempts = 0;
    this.lastRecoveryAttempt = null;
    this.stallRecoveryAttempts = 0;
    this.stallStartTime = null;
    this.stopStallWatchdog();
    this.hls?.destroy();

    setTimeout(() => {
      this.setup();
    }, 100);
  }

  // ==================== Stall Watchdog ====================

  private startStallWatchdog(): void {
    if (this.stallWatchdogInterval !== null) return;

    this.lastCurrentTime = this.video.currentTime;
    this.stallWatchdogInterval = window.setInterval(() => {
      this.checkForStall();
    }, this.STALL_CHECK_INTERVAL_MS);
  }

  private stopStallWatchdog(): void {
    if (this.stallWatchdogInterval !== null) {
      clearInterval(this.stallWatchdogInterval);
      this.stallWatchdogInterval = null;
    }
  }

  private checkForStall(): void {
    // Skip if video ended
    if (this.video.ended) {
      this.stallStartTime = null;
      return;
    }

    // Check if video is stuck waiting for data (readyState < HAVE_FUTURE_DATA)
    // This catches both playing stalls AND pause-due-to-buffer-underrun
    const isWaitingForData = this.video.readyState < 3;
    const currentTime = this.video.currentTime;
    const timeAdvanced = Math.abs(currentTime - this.lastCurrentTime) > 0.01;

    // If time is advancing normally, reset stall tracking
    if (timeAdvanced && !isWaitingForData) {
      this.lastCurrentTime = currentTime;
      this.stallStartTime = null;
      this.stallRecoveryAttempts = 0;
      return;
    }

    // If paused by user (not due to buffering), don't treat as stall
    if (this.video.paused && !isWaitingForData) {
      this.stallStartTime = null;
      return;
    }

    // Video is either: playing but not advancing, OR paused due to buffer underrun
    const now = performance.now();
    if (this.stallStartTime === null) {
      this.stallStartTime = now;
      this.lastCurrentTime = currentTime;
      return;
    }

    const stallDuration = now - this.stallStartTime;
    if (stallDuration >= this.STALL_THRESHOLD_MS) {
      this.recoverFromStall();
    }
  }

  private recoverFromStall(): void {
    this.stallRecoveryAttempts++;
    console.warn(
      `HLS.js stall watchdog: video frozen for 2s. Recovery attempt ${this.stallRecoveryAttempts}/${this.MAX_STALL_RECOVERY_ATTEMPTS}`,
    );

    if (this.stallRecoveryAttempts <= this.MAX_STALL_RECOVERY_ATTEMPTS) {
      // Try seeking to live sync position
      if (this.hls?.liveSyncPosition) {
        this.video.currentTime = this.hls.liveSyncPosition;
      }
      this.hls?.startLoad();
      void this.video.play().catch(() => {});
      this.stallStartTime = null;
    } else {
      console.warn("HLS.js stall watchdog: Max recovery attempts. Reloading stream.");
      this.reloadStream();
    }
  }

  private handleFragBuffered = (): void => {
    if (!this.hls) return;

    const hls = this.hls;
    const time = (performance.now() - this.debugStartTime) / 1000;
    const ewma = Math.round(hls.bandwidthEstimate / 1000);
    const actualLevel = hls.loadLevel >= 0 ? hls.loadLevel : hls.currentLevel;
    const levelBitrate =
      actualLevel >= 0 && hls.levels[actualLevel] ? Math.round(hls.levels[actualLevel].bitrate / 1000) : 0;

    const newPoint: BitrateDataPoint = {
      time,
      bitrate: levelBitrate,
      ewma,
      level: actualLevel,
    };

    const history = [...this.debugStats.bitrateHistory, newPoint];
    if (history.length > this.BITRATE_HISTORY_MAX_POINTS) {
      history.shift();
    }

    this.debugStats = {
      ...this.debugStats,
      bitrateHistory: history,
    };
  };

  // ==================== Aspect Ratio Detection ====================

  private detectAspectRatio(): void {
    const checkAndNotify = (source: string): boolean => {
      const width = this.video.videoWidth;
      const height = this.video.videoHeight;
      console.log(
        `[HlsJsPlayer] detectAspectRatio check (${source}): ${width}x${height}, readyState=${this.video.readyState}`,
      );

      if (width > 0 && height > 0) {
        const ratio = height > width ? "portrait" : "landscape";
        console.log(`[HlsJsPlayer] Detected aspect ratio: ${ratio} (${width}x${height})`);
        this.callbacks.onAspectRatioDetected(ratio);
        return true;
      }
      return false;
    };

    // Try immediately
    if (checkAndNotify("immediate")) {
      return;
    }

    // Fallback: listen for resize event (fires when video dimensions become available)
    const onResize = (): void => {
      if (checkAndNotify("resize")) {
        this.video.removeEventListener("resize", onResize);
      }
    };
    this.video.addEventListener("resize", onResize);

    // Additional fallback: poll briefly in case events don't fire
    let pollCount = 0;
    const pollInterval = setInterval(() => {
      pollCount++;
      if (checkAndNotify(`poll-${pollCount}`)) {
        clearInterval(pollInterval);
        this.video.removeEventListener("resize", onResize);
      } else if (pollCount >= 10) {
        console.warn("[HlsJsPlayer] Could not detect video dimensions after 2s, defaulting to landscape");
        clearInterval(pollInterval);
        this.video.removeEventListener("resize", onResize);
      }
    }, 200);
  }

  // ==================== Debug Stats ====================

  private updateDebugStats(): void {
    const quality = this.video.getVideoPlaybackQuality?.();
    if (quality) {
      this.debugStats = {
        ...this.debugStats,
        droppedFrames: quality.droppedVideoFrames ?? 0,
      };
    }

    if (this.hls) {
      const hls = this.hls;
      const levels: LevelInfo[] = (hls.levels || []).map((level: Level, index: number) => ({
        index,
        width: level.width || 0,
        height: level.height || 0,
        bitrate: level.bitrate || 0,
      }));

      const actualLevel = hls.loadLevel >= 0 ? hls.loadLevel : hls.currentLevel;

      this.debugStats = {
        ...this.debugStats,
        bandwidthEstimate: Math.round(hls.bandwidthEstimate / 1000),
        currentLevel: actualLevel,
        levels,
      };
    }
  }
}
